import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { saveBlobResponse, blobErrorDetail } from '../lib/download';
import { useAuth } from '../contexts/AuthContext';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, Legend, ReferenceArea, CartesianGrid,
} from 'recharts';
import { Loader2, ArrowLeft, FileDown, ClipboardCopy, AlertTriangle, CheckCircle2, HelpCircle, ArrowRight } from 'lucide-react';

const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(1)} %`);
const ci = (c) => (c ? `${(c[0] * 100).toFixed(1)}–${(c[1] * 100).toFixed(1)} %` : '—');
const num = (x) => (x == null ? '—' : x);
const fmtCell = (c, head) => {
  if (c === '' || c == null) return '—';
  if (/^[01]\.\d{4}$/.test(String(c)) && /Rate|Quote|Anteil/i.test(String(head))) return `${(parseFloat(c) * 100).toFixed(1)} %`;
  return String(c);
};

const Section = ({ id, title, sub, children }) => (
  <section className="bg-card border border-border rounded-sm p-5 space-y-3" data-testid={`clean-section-${id}`}>
    <div>
      <h2 className="text-sm font-medium text-foreground">{title}</h2>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
    {children}
  </section>
);

const Grid = ({ rows, testId, right = true }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-[11px]">
      <thead>
        <tr className="text-left text-muted-foreground uppercase tracking-wider text-[9px]">
          {rows[0].map((h, i) => <th key={i} className={`py-1.5 pr-3 font-normal ${i > 0 && right ? 'text-right' : ''}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.slice(1).map((r, ri) => (
          <tr key={ri} className="border-t border-border/60">
            {r.map((c, i) => <td key={i} className={`py-1.5 pr-3 ${i > 0 && right ? 'text-right font-mono' : ''} ${String(c).startsWith('−') ? 'text-amber-300' : ''}`}>{fmtCell(c, rows[0][i])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const VARIANT_ROWS = [
  ['geschriebene Briefe', (v) => v.letters_written], ['versendete Briefe', (v) => v.letters_sent], ['entschiedene Briefe', (v) => v.letters_decided],
  ['Deals', (v) => v.deals], ['Rohscans', (v) => v.scans_raw], ['− System-Test/Bot', (v) => v.scans_system_test], ['− vor Versand', (v) => v.scans_before],
  ['− am Versandtag', (v) => v.scans_same_day], ['bereinigte Scans', (v) => v.scans_clean], ['eindeutig gescannte Briefe', (v) => v.letters_scanned],
  ['Scan-Rate (÷ versendet)', (v) => `${pct(v.scan_rate)} · ${v.letters_scanned}/${v.letters_sent}`],
  ['Responses gesamt', (v) => v.responses], ['Inbound', (v) => v.inbound], ['Call-getriggert', (v) => v.call_triggered],
  ['davon selbst zurückgemeldet nach Call', (v) => v.call_self_reported], ['Inbound nach Call anderer Person', (v) => v.inbound_after_other_call],
  ['positive Responses', (v) => v.positive], ['positiv Inbound', (v) => v.positive_inbound], ['positiv Call-getriggert', (v) => v.positive_call],
  ['Inbound-Rate (÷ entschieden)', (v) => `${pct(v.inbound_rate)} · ${v.inbound}/${v.letters_decided}`], ['Inbound-Rate CI95 (Wilson)', (v) => ci(v.inbound_rate_ci)],
  ['positive Inbound-Rate', (v) => `${pct(v.positive_inbound_rate)} · ${v.positive_inbound}/${v.letters_decided}`], ['pos. Inbound CI95', (v) => ci(v.positive_inbound_rate_ci)],
  ['Inbound-Rate (÷ versendet)', (v) => `${pct(v.inbound_rate_on_sent)} · ${v.inbound_on_sent}/${v.letters_sent}`],
  ['Calls von Max', (v) => v.calls], ['Deals angerufen', (v) => `${v.deals_called} (${pct(v.deals_called_share)})`],
  ['Briefklasse (Prio)', (v) => Object.entries(v.prio_split).map(([k, n]) => `${k}: ${n}`).join(' · ') || '—'],
  ['Verfasser', (v) => Object.entries(v.author_split).map(([k, n]) => `${k}: ${n}`).join(' · ') || '—'],
  ['belastbar (≥ 25 entschieden)', (v) => (v.reliable ? 'ja' : 'nein')],
];

function ExperimentBlock({ e }) {
  return (
    <div className="space-y-3" data-testid={`clean-ab-${e.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm text-foreground">{e.name}</h3>
        <span className="text-[10px] font-mono text-muted-foreground">{e.status} · Ziel {num(e.target_n)} je Variante · Fenster {e.window_days} d</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-muted-foreground uppercase tracking-wider text-[9px]"><th className="py-1.5 pr-3 font-normal">Kennzahl</th>
            {e.variants.map((v) => <th key={v.id} className="py-1.5 pr-3 font-normal text-right">{v.name}</th>)}</tr></thead>
          <tbody>
            {VARIANT_ROWS.map(([label, fn]) => (
              <tr key={label} className="border-t border-border/60"><td className="py-1.5 pr-3">{label}</td>
                {e.variants.map((v) => <td key={v.id} className="py-1.5 pr-3 text-right font-mono">{num(fn(v))}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      <Grid testId={`clean-ab-pairs-${e.id}`} rows={[['Vergleich', 'p Inbound (Fisher, 2-seitig)', 'p pos. Inbound', 'p Scan-Rate', 'belastbar', 'nötige n je Variante'],
        ...e.pairs.map((p) => [`${p.a} vs. ${p.b}`, num(p.p_inbound), num(p.p_positive_inbound), num(p.p_scan), p.reliable ? 'ja' : 'nein — Stichprobe zu klein', num(p.required_n_per_variant)])]} />
      <div className="grid sm:grid-cols-3 gap-2 text-[11px]" data-testid={`clean-ab-decision-${e.id}`}>
        <div className="border border-border rounded-sm p-2"><div className="text-[9px] uppercase text-muted-foreground">Entscheidung laut System</div>{e.decision_system}</div>
        <div className="border border-border rounded-sm p-2"><div className="text-[9px] uppercase text-muted-foreground">nach Bereinigung</div>{e.decision_clean}</div>
        <div className={`border rounded-sm p-2 ${e.decision_changed ? 'border-amber-500/50 text-amber-200' : 'border-border'}`}><div className="text-[9px] uppercase text-muted-foreground">geändert</div>{e.decision_changed ? 'ja' : 'nein'}</div>
      </div>
      <p className="text-[11px] text-muted-foreground">Verzerrungsprüfung: Inbound-Rate auf entschiedenen Briefen {pct(e.bias_check.inbound_rate_decided)} vs. auf allen versendeten {pct(e.bias_check.inbound_rate_sent)} ({e.bias_check.open_letters} offene Briefe).</p>
    </div>
  );
}

function CohortChart({ rows }) {
  const data = rows.map((r) => ({ week: r.week.slice(2), inbound: r.inbound_rate != null ? +(r.inbound_rate * 100).toFixed(1) : null,
    call: r.call_rate != null ? +(r.call_rate * 100).toFixed(1) : null, total: r.total_rate != null ? +(r.total_rate * 100).toFixed(1) : null, n: r.sent, young: r.young }));
  const firstYoung = data.find((d) => d.young);
  return (
    <div className="h-64" data-testid="clean-cohort-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="week" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} unit="%" />
          <RTooltip contentStyle={{ fontSize: 11 }} formatter={(v, name, p) => [`${v} %`, `${name} (n=${p.payload.n})`]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {firstYoung && <ReferenceArea x1={firstYoung.week} x2={data[data.length - 1].week} fill="rgba(251,191,36,0.10)" label={{ value: 'junge Kohorten', fontSize: 10, fill: '#fbbf24' }} />}
          <Line type="linear" dataKey="inbound" name="Inbound-Rate" stroke="#34d399" dot={{ r: 2 }} connectNulls />
          <Line type="linear" dataKey="call" name="Call-getriggerte Rate" stroke="#60a5fa" dot={{ r: 2 }} connectNulls />
          <Line type="linear" dataKey="total" name="Gesamt-Rate" stroke="#f59e0b" dot={{ r: 2 }} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const Bullets = ({ icon: Icon, title, items, cls, testId }) => (
  <div data-testid={testId}>
    <div className={`flex items-center gap-1.5 text-[11px] font-medium ${cls}`}><Icon className="w-3.5 h-3.5" /> {title}</div>
    <ul className="mt-1.5 space-y-1 text-[11px] text-foreground/90 list-disc pl-4">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
  </div>
);

export default function CleanOutreachStatsPage() {
  const navigate = useNavigate();
  const { isAcMember } = useAuth();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getCleanOutreachStats().then((r) => setRep(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Auswertung konnte nicht geladen werden'));
  }, []);

  const download = async () => {
    setBusy(true);
    try { saveBlobResponse(await api.exportCleanOutreachStats(), 'OLTA_Outreach_bereinigt.zip'); toast.success('CSV-Tabellen + Report heruntergeladen'); }
    catch (e) { toast.error((await blobErrorDetail(e)) || 'Download fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  const copyMd = async () => {
    try { const r = await api.getCleanOutreachMarkdown(); await navigator.clipboard.writeText(r.data); toast.success('Report als Markdown kopiert'); }
    catch { toast.error('Kopieren fehlgeschlagen'); }
  };

  if (isAcMember) return <div className="text-sm text-muted-foreground p-8">Nicht verfügbar.</div>;
  if (err) return <div className="text-sm text-red-300 p-8" data-testid="clean-stats-error">{err}</div>;
  if (!rep) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;

  const t = rep.tables; const b = rep.data_basis; const d = rep.definitions; const o = rep.overall; const c = rep.comparison; const rt = rep.response_time;
  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="clean-stats-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => navigate('/outreach-stats')} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="clean-back-btn"><ArrowLeft className="w-3 h-3" /> Outreach-Statistik</button>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight mt-1">Bereinigte Brief-Auswertung</h1>
          <p className="text-muted-foreground text-sm mt-1">Nur echte Daten, keine Schätzungen · Bereinigung nur in der Auswertung · Stand {rep.generated_at.slice(0, 16).replace('T', ' ')} UTC</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyMd} data-testid="clean-copy-md-btn" className="inline-flex items-center gap-1.5 text-[11px] border border-border hover:bg-secondary/40 rounded-sm px-2.5 py-1.5"><ClipboardCopy className="w-3 h-3" /> Report als Markdown kopieren</button>
          <button onClick={download} disabled={busy} data-testid="clean-download-btn" className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300 border border-emerald-700/50 hover:bg-emerald-500/10 rounded-sm px-2.5 py-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} CSV-Tabellen (ZIP)</button>
        </div>
      </div>

      <Section id="summary" title="Zusammenfassung">
        <ol className="list-decimal pl-5 space-y-1 text-[12px] text-foreground/90" data-testid="clean-summary">{rep.summary.map((s, i) => <li key={i}>{s}</li>)}</ol>
      </Section>

      <Section id="basis" title="1. Datenbasis" sub="Tabellen und Felder, Definitionen, erkannte Lücken">
        <ul className="text-[11px] space-y-1 text-foreground/90" data-testid="clean-definitions">
          {Object.entries(d.tables).map(([k, v]) => <li key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-mono text-[10px]">{v}</span></li>)}
          <li><span className="text-muted-foreground">Entschieden:</span> {d.decided}</li>
          <li><span className="text-muted-foreground">Positive Response:</span> {d.positive}</li>
          <li><span className="text-muted-foreground">Geschrieben:</span> {d.written}</li>
          <li><span className="text-muted-foreground">Call von Max:</span> {d.max_call} — erkannt als: {b.max_identity.join(', ')}; andere Anrufer: {Object.entries(b.other_callers).map(([k, n]) => `${k} (${n})`).join(', ') || 'keine'}</li>
          <li><span className="text-muted-foreground">Scans:</span> {d.scan_rules}</li>
          <li><span className="text-muted-foreground">Bewertungen:</span> {Object.entries(b.sentiments).map(([k, n]) => `${k}: ${n}`).join(' · ') || '—'} · <span className="text-muted-foreground">Kanäle:</span> {Object.entries(b.channels).map(([k, n]) => `${k}: ${n}`).join(' · ') || '—'}</li>
          <li><span className="text-muted-foreground">Ausgeschlossen:</span> {b.emails_excluded} E-Mails · {b.broker_excluded} Briefe aus Broker-Deals · Break-ups separat: {b.breakups.letters} ({b.breakups.sent} versendet, {b.breakups.responded_after} mit Response danach)</li>
          <li className="text-amber-200"><span className="text-muted-foreground">Briefe ohne Versanddatum:</span> {rep.issues.find((i) => i.key === 'sent_without_date')?.count || 0} · <span className="text-muted-foreground">nicht zuordenbare Responses:</span> {o.responses_unassignable}</li>
        </ul>
      </Section>

      <Section id="reconciliation" title="3. Bereinigungsübersicht">
        <div className="grid lg:grid-cols-2 gap-6">
          <Grid testId="clean-recon-letters" rows={t['01_ueberleitung_briefe']} />
          <Grid testId="clean-recon-scans" rows={t['02_ueberleitung_scans']} />
        </div>
      </Section>

      <Section id="ab" title="4. A/B-Tests" sub="Alle entschiedenen Briefe je Test und Variante · Wilson-95 %-Intervalle · exakter Fisher-Test (zweiseitig)">
        {rep.ab_tests.length === 0 && <p className="text-[11px] text-muted-foreground">Keine Experimente im System.</p>}
        <div className="space-y-8">{rep.ab_tests.map((e) => <ExperimentBlock key={e.id} e={e} />)}</div>
      </Section>

      <Section id="cohorts" title="5. Entwicklung der Response-Rate" sub={`Wöchentliche Versandkohorten · Reaktionszeit: Median ${num(rt.median_days)} d, P90 ${num(rt.p90_days)} d (n=${rt.n}) · Schwelle „jung“: ${num(rt.threshold_days)} d (${rt.threshold_rule})`}>
        {rep.cohorts.length ? <CohortChart rows={rep.cohorts} /> : <p className="text-[11px] text-muted-foreground">Keine versendeten Briefe mit Datum.</p>}
        <Grid testId="clean-cohorts-table" rows={t['05_kohorten']} />
        {rep.cohort_variation && <p className="text-[11px] text-muted-foreground">Reife Kohorten, erste vs. zweite Hälfte: {pct(rep.cohort_variation.first_half.rate)} ({rep.cohort_variation.first_half.inbound}/{rep.cohort_variation.first_half.sent}) vs. {pct(rep.cohort_variation.second_half.rate)} ({rep.cohort_variation.second_half.inbound}/{rep.cohort_variation.second_half.sent}), Fisher p={num(rep.cohort_variation.p_fisher)}</p>}
      </Section>

      <Section id="comparison" title="6. Positive Responses: Calls vs. andere Kanäle" sub={`Rückläufer-Deals: ${c.other_channels.returned_deals} (${c.other_channels.returned_letters} Briefe abgezogen) · Calls anderer Personen: ${c.calls.other_calls}`}>
        <Grid testId="clean-comparison" rows={t['06_positive_gegenueberstellung']} />
        <p className="text-[11px] text-muted-foreground">Call-Auswahl: {pct(rep.selection.share_called_among_scanned)} der gescannten vs. {pct(rep.selection.share_called_among_unscanned)} der nicht gescannten Deals wurden angerufen (Fisher p={num(rep.selection.p_fisher)}).</p>
      </Section>

      <Section id="interpretation" title="7. Interpretation">
        <div className="grid lg:grid-cols-3 gap-5">
          <Bullets icon={CheckCircle2} title="Belegt durch die Daten" items={rep.interpretation.belegt} cls="text-emerald-300" testId="clean-proven" />
          <Bullets icon={HelpCircle} title="Hypothese, noch nicht belegt" items={rep.interpretation.hypothesen} cls="text-amber-300" testId="clean-hypotheses" />
          <Bullets icon={ArrowRight} title="Empfohlene Konsequenz / nächster Test" items={rep.interpretation.empfehlungen} cls="text-sky-300" testId="clean-next" />
        </div>
      </Section>

      <Section id="issues" title="8. Datenprobleme und Annahmen">
        {rep.issues.length ? <Grid testId="clean-issues" rows={t['07_datenprobleme']} right={false} /> : <p className="text-[11px] text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> keine Auffälligkeiten</p>}
        <p className="text-[11px] text-muted-foreground">Annahmen: Response → zuletzt davor versendeter Brief des Deals · Calls je Deal/Person/Tag einmal · Scans am Versandtag ausgeschlossen (abweichend von der System-Regel seit 26.09.2026) · „entschieden“ nach Systemdefinition ({b.system_window_days} Tage).</p>
      </Section>
    </div>
  );
}
